/**
 * A read-only preview of what this upload will actually do.
 *
 * EVERY LINE IS A FACT THE BACKEND WILL PRODUCE, not a description of what
 * usually happens. Each one was checked against
 * functions/src/policies/saveDraft.ts:
 *
 *   Owner       `ownerId: context.uid` — always the person uploading. There
 *               is no owner field on the form and none is needed.
 *   Number      `allocateAndClaimPolicyNumber` runs inside the create, from
 *               the organization's configured format. It CANNOT be previewed
 *               here: `previewPolicyNumber` is admin-only and this page is an
 *               author's, and a preview would not be a claim anyway — two
 *               uploads in flight would both show the same number and one of
 *               them would be wrong. So the line says when it is assigned.
 *   Review      `reviewCycleMonths` is stored on the draft; `nextReviewDate`
 *               is null until publication, which is why the line says
 *               "from publication" rather than naming a date.
 *   Acknowledge `audienceFields` gives 'departments' only when a department
 *               is chosen AND that radio is selected; otherwise 'all'.
 *   Workflow    From `previewApprovalRouteFn`, which is the resolver the
 *               submission itself uses — so the preview and the submission
 *               cannot disagree. Routing precedence is NOT reimplemented
 *               here; if the call fails the line is left out rather than
 *               guessed.
 *
 * What is deliberately absent: the acknowledgement deadline. It is
 * `input.acknowledgementDays || settings.defaultAcknowledgementDays`, this
 * page sends nothing, and the organization default is not readable from an
 * author's session without a new read. Saying "30 days" would be a guess.
 */

import { useAuth } from '../../auth/useAuth';
import { useRoutePreview } from '../approvals/useApprovals';
import { REVIEW_CYCLES } from './ReviewAndApproval';
import type { Audience } from './TaxonomyFields';
import own from './UploadPage.module.css';

interface Props {
  reviewCycle: number;
  requiresAck: boolean;
  audience: Audience;
  departmentId: string;
  departmentName?: string;
  documentTypeId: string;
  submitForReview: boolean;
}

export function WhatHappensNext({
  reviewCycle, requiresAck, audience, departmentId, departmentName, documentTypeId, submitForReview,
}: Props) {
  const { session } = useAuth();

  // Only worth asking when the answer is going to be used. The same shape the
  // approvals page sends, answered by the same resolver.
  const route = useRoutePreview(
    { documentTypeId: documentTypeId || null, departmentId: departmentId || null },
    submitForReview,
  );

  const cycle = REVIEW_CYCLES.find((c) => c.value === reviewCycle)?.label ?? `Every ${reviewCycle} months`;
  const owner = session?.displayName?.trim() || session?.email || 'You';

  const acknowledgement = !requiresAck
    ? 'Not required'
    : audience === 'departments' && departmentId
      ? `Only ${departmentName ?? 'the chosen department'}`
      : 'All staff in the organization';

  const workflowName = route.data?.route?.workflowName ?? null;

  return (
    <section className={own.next} aria-labelledby="next-title">
      <h2 className={own.panelTitle} id="next-title">What happens next</h2>
      <dl className={own.nextList}>
        <div className={own.nextRow}>
          <dt className={own.nextTerm}>Owner</dt>
          <dd className={own.nextValue}>{owner}</dd>
        </div>
        <div className={own.nextRow}>
          <dt className={own.nextTerm}>Policy number</dt>
          <dd className={own.nextValue}>Assigned on upload</dd>
        </div>
        <div className={own.nextRow}>
          <dt className={own.nextTerm}>Review</dt>
          <dd className={own.nextValue}>{cycle}, from publication</dd>
        </div>
        <div className={own.nextRow}>
          <dt className={own.nextTerm}>Acknowledgement</dt>
          <dd className={own.nextValue}>{acknowledgement}</dd>
        </div>
        <div className={own.nextRow}>
          <dt className={own.nextTerm}>Submission</dt>
          <dd className={own.nextValue}>
            {submitForReview ? 'Sent for approval' : 'Saved as a draft'}
          </dd>
        </div>
        {/* Only when routing has actually answered. A failed or pending
            preview leaves the row out; it never shows a guess. */}
        {submitForReview && workflowName && (
          <div className={own.nextRow}>
            <dt className={own.nextTerm}>Approval workflow</dt>
            <dd className={own.nextValue}>{workflowName}</dd>
          </div>
        )}
      </dl>
      {submitForReview && !workflowName && !route.isLoading && (
        <p className={own.hint}>
          The approval route is shown on the policy once it has been submitted.
        </p>
      )}
    </section>
  );
}
